import { Favorite, FavoriteBorder } from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import Dropdown from "../../components/dropdown/dropdown";
import MediaTypeIcon from "../../components/media-format/media-type-icon";
import { Asset } from "../../generated/gql/graphql";
import useCollection from "../../hooks/mutation/collection/use-collection";
import { AssetListContext } from "../../providers/asset-list-provider";
import { ConfirmationModalType } from "../../types/confirm-modal";

type Props = {
  asset: Asset;
  category?: string;
  collectionId: string;
  rawCollectionId?: string;
  setConfirm: (val: ConfirmationModalType) => void;
};

export default function CollectionAssetGridItem(props: Props): ReactElement {
  const { asset, collectionId, category, rawCollectionId, setConfirm } = props;

  const { selectedFiles, setSelectedFile } = useContext(AssetListContext);
  const [selected, setSelected] = useState(selectedFiles.includes(asset));

  const { updateCollectionThumbnail } = useCollection();

  const navigate = useNavigate();

  useEffect(() => {
    setSelected(selectedFiles.includes(asset));
  }, [selectedFiles]);

  async function onFavorite(favourite: boolean): Promise<void> {
    // eslint-disable-next-line no-console
    console.log("Toggle Favorite", favourite);

    if (!rawCollectionId || !asset.id) {
      return;
    }

    try {
      await updateCollectionThumbnail({
        assetId: asset.id,
        collectionId: rawCollectionId,
      });
    } catch (error) {
      console.error(error);
    }
  }

  function onClick(): void {
    if (!asset.id) {
      return;
    }

    navigate(`/file/${asset.id}/1`);
  }

  function onSelect(): void {
    setSelectedFile(asset, !selected);
    setSelected(!selected);
  }

  return (
    <Card
      key={asset.id}
      id={asset.id || ""}
      style={{ borderColor: "#E8E8E8", boxShadow: "none" }}
      className="shadow-none min-h-[350px] rounded-[15px] flex flex-col border-solid border-1 w-full max-w-[calc(50%_-_15px)] HD:max-w-[calc(25%_-_15px)] HD+:max-w-[calc(20%_-_16px)] QHD:max-w-[calc(10%_-_18px)]"
    >
      <CardHeader
        avatar={
          <>
            <Checkbox
              sx={{ "& .MuiSvgIcon-root": { fontSize: 22 } }}
              key={asset.id}
              id={asset.id || ""}
              value={asset.id}
              onChange={onSelect}
              checked={selected}
            />
            <Checkbox
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onFavorite(event.target.checked)
              }
              defaultChecked={asset.favorite || false}
              icon={<FavoriteBorder />}
              checkedIcon={<Favorite />}
            />
          </>
        }
        action={
          <Dropdown
            onConfirm={setConfirm}
            asset={asset}
            setCollectionItem
            classes="mt-6"
            collectionId={collectionId ?? undefined}
          />
        }
      />
      <div
        onClick={onClick}
        className="mx-[20px] h-full flex items-center justify-center cursor-pointer"
      >
        <MediaTypeIcon
          asset={asset}
          classes="text-[120px] max-h-[200px] object-contain"
        />
      </div>
      <CardContent className="p-[20px] leading-[17px] last:pb-[10px]">
        <Typography className="mb-[10px] text-[14px] break-keep">
          {asset.name}
        </Typography>
        <Typography className="font-light border-[#4a4a4a] text-[14px]">
          {asset.contentAmount}
          {asset.contentUnit}
          {category && ` • ${category}`}
        </Typography>
      </CardContent>
    </Card>
  );
}
